/**
 * AI视频创作平台 - 路由配置
 * 更新时间: 2026-01-28
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { buildDocumentTitle } from '@shared/constants/brand';
import { useUserStore } from '@/stores/user';
import { useAdminStore } from '@/stores/admin';
import { adminRoutes } from './admin';

// 路由元信息类型定义
declare module 'vue-router' {
  interface RouteMeta {
    /** 页面标题 */
    title?: string;
    /** 是否需要登录 */
    requiresAuth?: boolean;
    /** 是否隐藏布局 */
    hideLayout?: boolean;
    /** 是否需要管理员登录 */
    requiresAdminAuth?: boolean;
    /** 是否隐藏管理后台布局 */
    hideAdminLayout?: boolean;
    /** 需要的权限 */
    permission?: string;
    /** 菜单图标 */
    icon?: string;
  }
}

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/architecture-demo/features/homepage').then((module) => module.HomePage),
    meta: {
      title: '首页',
      requiresAuth: false,
      hideLayout: true,
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: {
      title: '登录',
      requiresAuth: false,
      hideLayout: true
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/Register.vue'),
    meta: {
      title: '注册',
      requiresAuth: false,
      hideLayout: true
    }
  },
  {
    path: '/storyboard',
    name: 'Storyboard',
    component: () => import('@/views/storyboard/Index.vue'),
    meta: {
      title: '故事版',
      requiresAuth: true
    }
  },
  {
    path: '/story/upload',
    name: 'CreateStory',
    component: () => import('@/views/story/Upload.vue'),
    meta: {
      title: '创建故事',
      requiresAuth: true
    }
  },
  {
    path: '/story/:id/upload',
    name: 'UploadStory',
    component: () => import('@/views/story/Upload.vue'),
    meta: {
      title: '上传故事',
      requiresAuth: true
    }
  },
  {
    path: '/story/:id/entities',
    name: 'EntityManage',
    component: () => import('@/views/story/Entities.vue'),
    meta: {
      title: '主体管理',
      requiresAuth: true
    }
  },
  {
    path: '/story/:id/scenes',
    name: 'SceneManage',
    component: () => import('@/views/story/Scenes.vue'),
    meta: {
      title: '分镜管理',
      requiresAuth: true
    }
  },
  {
    path: '/story/:id/preview',
    name: 'Preview',
    component: () => import('@/views/story/Preview.vue'),
    meta: {
      title: '成片预览',
      requiresAuth: true
    }
  },
  {
    path: '/story/:id/output',
    name: 'Output',
    component: () => import('@/views/story/Output.vue'),
    meta: {
      title: '最终输出',
      requiresAuth: true
    }
  },
  {
    path: '/collection/:id',
    name: 'CollectionHome',
    component: () => import('@/views/collection/CollectionHome.vue'),
    meta: {
      title: '故事集',
      requiresAuth: true
    }
  },
  {
    path: '/collection/:id/upload-novel',
    name: 'CollectionUploadNovel',
    component: () => import('@/views/collection/CollectionUploadNovel.vue'),
    meta: {
      title: '上传小说',
      requiresAuth: true
    }
  },
  {
    path: '/collection/:id/export',
    name: 'CollectionExport',
    component: () => import('@/views/collection/CollectionExport.vue'),
    meta: {
      title: '批量下载',
      requiresAuth: true
    }
  },
  {
    path: '/image-editor',
    redirect: '/image-studio?tab=edit',
    meta: {
      title: '图片编辑',
      requiresAuth: true
    }
  },
  {
    path: '/assets',
    name: 'Assets',
    component: () => import('@/views/assets/Index.vue'),
    meta: {
      title: '素材库',
      requiresAuth: true
    }
  },
  {
    path: '/image-studio',
    name: 'ImageStudio',
    component: () => import('@/views/image-studio/Index.vue'),
    meta: {
      title: '图片生成',
      requiresAuth: true
    }
  },
  {
    path: '/plaza',
    name: 'Plaza',
    component: () => import('@/views/plaza/Index.vue'),
    meta: {
      title: '作品广场',
      requiresAuth: false
    }
  },
  {
    path: '/plaza/:id',
    name: 'WorkDetail',
    component: () => import('@/views/plaza/Detail.vue'),
    meta: {
      title: '作品详情',
      requiresAuth: false
    }
  },
  {
    path: '/feedback',
    name: 'Feedback',
    component: () => import('@/views/Feedback.vue'),
    meta: {
      title: '意见反馈',
      requiresAuth: false
    }
  },
  {
    path: '/changelog',
    name: 'Changelog',
    component: () => import('@/views/Changelog.vue'),
    meta: {
      title: '更新历史',
      requiresAuth: false
    }
  },
  {
    path: '/user/:id',
    name: 'UserProfile',
    component: () => import('@/views/user/Profile.vue'),
    meta: {
      title: '用户主页',
      requiresAuth: false
    }
  },
  {
    path: '/user/center',
    name: 'UserCenter',
    component: () => import('@/views/user/Center.vue'),
    meta: {
      title: '个人中心',
      requiresAuth: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/error/NotFound.vue'),
    meta: {
      title: '页面不存在'
    }
  },
  // 管理后台路由
  ...adminRoutes
];

// 创建路由实例
const router = createRouter({
  history: createWebHistory(),
  routes,
  // 滚动行为
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  }
});

// 路由守卫 - 设置页面标题
router.beforeEach(async (to, _from, next) => {
  // 设置页面标题
  const title = to.meta.title as string;
  const isAdminRoute = to.path.startsWith('/csdadmin');
  document.title = buildDocumentTitle(title, isAdminRoute);

  // 管理后台路由认证检查
  if (isAdminRoute) {
    const adminStore = useAdminStore();

    // 管理员登录页不需要认证
    if (to.name === 'AdminLogin') {
      // 已登录管理员访问登录页，跳转到仪表盘
      if (adminStore.isLoggedIn) {
        next({ name: 'AdminDashboard' });
        return;
      }
      next();
      return;
    }

    // 其他管理后台页面需要认证
    if (to.meta.requiresAdminAuth !== false) {
      // 未登录，跳转到登录页
      if (!adminStore.isLoggedIn) {
        next({ name: 'AdminLogin', query: { redirect: to.fullPath } });
        return;
      }

      // 已登录但还没有管理员信息，先获取
      if (!adminStore.admin) {
        try {
          await adminStore.initAdminState();
        } catch (error) {
          console.error('获取管理员信息失败:', error);
          // 获取失败，清除 token 并跳转到登录页
          adminStore.clearAdmin();
          next({ name: 'AdminLogin', query: { redirect: to.fullPath } });
          return;
        }
      }

      // 权限检查
      const permission = to.meta.permission as string | undefined;
      if (permission && !adminStore.hasPermission(permission)) {
        // 无权限，跳转到仪表盘
        next({ name: 'AdminDashboard' });
        return;
      }
    }

    next();
    return;
  }

    // 用户认证检查
  const userStore = useUserStore();

  // 不再自动跳转登录页，让页面正常访问
  // 需要登录的操作会在具体操作时弹出登录弹窗

  // 已登录用户访问登录/注册页，跳转到首页
  if ((to.name === 'Login' || to.name === 'Register') && userStore.isLoggedIn) {
    next({ name: 'Storyboard' });
    return;
  }

  next();
});

export default router;
